<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaHeader from '../base/GaiaHeader.vue';
import GaiaText from '../base/GaiaText.vue';
import type { gaiaHeadingSize } from '../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /** Second line under the title — a file path, a counterparty, a count. */
  subtitle: {
    type: String,
    default: undefined,
  },
  /**
   * Title size. `record` by default; `screen` for a record page whose title
   * leads a summary paragraph (Contract View mock), where it carries the page.
   */
  size: {
    type: String as PropType<keyof typeof gaiaHeadingSize>,
    default: 'record',
  },
});
</script>

<template>
  <!-- The detail-page title row: title with an inline #meta run (status, due
       date, a chip), and #actions pinned right. No back arrow: the way up is
       the layout's breadcrumb bar, so the row never duplicates it. Replaces
       the hand-rolled `n-page-header` the older detail views each built their
       own way, so the title, its meta and the action buttons keep one set of
       sizes and one baseline across pages. -->
  <n-flex vertical :size="8" :style="{ minWidth: 0 }">
    <n-flex
      :align="$slots.eyebrow ? 'flex-start' : 'center'"
      :size="12"
      :wrap="false"
      :style="{ minWidth: 0 }"
    >
      <n-flex vertical :size="$slots.eyebrow ? 6 : 2" :style="{ flex: '1 1 auto', minWidth: 0 }">
        <!-- #eyebrow: the caps kind line above the title, with its count pill. -->
        <n-flex v-if="$slots.eyebrow" align="center" :size="8" :wrap="false">
          <slot name="eyebrow" />
        </n-flex>
        <n-flex align="center" :size="10" :wrap="false" :style="{ minWidth: 0 }">
          <!-- `record` by default, not `screen`: this titles one document, and a
             review's title is a filename that has to be allowed to wrap. -->
          <gaia-header :level="1" :size="props.size" :style="{ minWidth: 0 }">
            {{ props.title }}
          </gaia-header>
          <n-flex
            v-if="$slots.meta"
            align="center"
            :size="8"
            :wrap="false"
            :style="{ flex: 'none' }"
          >
            <slot name="meta" />
          </n-flex>
        </n-flex>
        <gaia-text v-if="props.subtitle" size="meta" tone="subtle" truncate>
          {{ props.subtitle }}
        </gaia-text>
      </n-flex>

      <n-flex
        v-if="$slots.actions"
        align="center"
        justify="end"
        :size="8"
        :wrap="false"
        :style="{ flex: 'none' }"
      >
        <slot name="actions" />
      </n-flex>
    </n-flex>
    <!-- #summary: a wrapping paragraph under the title row (the record's
       summary), across the full width — under the actions too — where the
       subtitle is a single truncated line beside them. -->
    <slot name="summary" />
  </n-flex>
</template>
